/* eslint-disable complexity */
export type TDateFormat =
  | 'd/m/yy'
  | 'm/d/yy'
  | 'd/mm/yy'
  | 'dd/mm/yy'
  | 'dd/mm/yyyy'
  | 'dd M yy'
  | 'dd M yyyy'
  | 'dd MM yy'
  | 'dd MM yyyy'
  | 'D dd M yy'
  | 'D dd M yyyy'
  | 'DD dd MM yy'
  | 'DD dd MM yyyy'
  | 'D, dd M yy'
  | 'D, dd M yyyy'
  | 'DD, dd MM yy'
  | 'DD, dd MM yyyy';

export type GoDateLayout =
  | '02/01/06'
  | '_2/_1/06'
  | '_1/_2/06'
  | '02 Jan 06'
  | 'Mon, 02 Jan 06'
  | 'Mon, 02 Jan 2006';

export type TTimeFormat = 'HH:mm' | 'HH:mm a' | 'HH:mm:ss' | 'HH:mm:ss a';
export type GoTimeLayout = '15:04' | '03:04 PM' | '15:04:05' | '03:04:05 PM';

export type CustomDateTimeFormatOptions = Omit<
  Intl.DateTimeFormatOptions,
  'timeZone'
> & { timezone?: string; locale?: string };

export type DateOptions = {
  locale?: string;
  dateFormat?: TDateFormat | GoDateLayout;
  timeFormat?: TTimeFormat | GoTimeLayout;
  timezone?: string;

  /**
   * Show the time in the formatted date.
   *
   * @default false - But will be set to true when `DateOptions.timeFormat` is provided
   */
  showTime?: boolean;
  /**
   * Show the date in the formatted date.
   *
   * @default true
   */
  showDate?: boolean;
  /**
   * This is old format (general settings), if configuration still using old settings then this option parameter can be used
   */
  formatPreset?: CustomDateTimeFormatOptions;
};

const getGeneralSettingDateFormat = (): DateOptions => {
  const { consoleSettings, generalSetting } = JSON.parse(
    localStorage.getItem('user') ?? '{}',
  );

  const { dateFormat, timeFormat, timezone } =
    consoleSettings ?? generalSetting ?? {};

  let formatOptions: DateOptions = {};

  /*
   * The condition "timeFormat === null" because there's a condition where
   * timeFormat is undefined or boolean value (based on project)
   */
  if (!dateFormat || timeFormat === null || !timezone) {
    return {};
  } else if (typeof dateFormat === 'string') {
    formatOptions = {
      dateFormat: dateFormat as DateOptions['dateFormat'],
      timeFormat,
      timezone,
    };
  } else {
    formatOptions = {
      formatPreset: {
        ...dateFormat,
        weekday: dateFormat?.weekday ? dateFormat.weekday : undefined,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: timeFormat,
        timezone,
      },
    };
  }

  return formatOptions;
};

export const formatDate = (
  date: Date,
  options?: DateOptions,
  /**
   * If general settings is provided, automatically use general settings
   * Turn it off if you want to avoid general settings usage even when general settings is available
   *
   * Just ignore this parameter if your project doesn't utilize general settings
   *
   * Note: DateOptions has higher priority than this option. When DateOptions specified by user, it will overides General Settings.
   */
  useGeneralSettings = true,
): string => {
  const DEFAULT_DATE_OPTIONS: DateOptions = {
    locale: 'en-gb',
    dateFormat: 'dd/mm/yy',
    timeFormat: 'HH:mm',
    showDate: true,
  };

  const generalSetting = useGeneralSettings
    ? getGeneralSettingDateFormat()
    : null;

  options ??= {};
  const {
    showDate = true,
    showTime = false,
    dateFormat = generalSetting?.dateFormat ?? DEFAULT_DATE_OPTIONS.dateFormat, // Here dateFormat will use: guven options, then generalSettings, lastly the default
    timezone = generalSetting?.timezone ?? DEFAULT_DATE_OPTIONS.timezone, // Here timeFormat will use: given options, then generalSettings, lastly the default
  } = options;

  let {
    locale = generalSetting?.locale ?? DEFAULT_DATE_OPTIONS.locale,
    timeFormat = generalSetting?.timeFormat ?? DEFAULT_DATE_OPTIONS.timeFormat,
  } = options;

  // If formatPreset is provided, override all config above to this preset (generated from old structure of general setting)
  if (generalSetting?.formatPreset) {
    const {
      locale: formatPresetLocale,
      timezone: formatPresetTimezone,

      hour,
      minute,
      second,
      hour12,

      ...rest
    } = generalSetting.formatPreset;

    // Show time when util's consumer explicitly write `showTime: true`
    const formatPresetTimeFormat = showTime
      ? { hour, minute, second, hour12 }
      : {};

    const formatted = date.toLocaleString(formatPresetLocale, {
      ...(formatPresetTimezone ? { timeZone: formatPresetTimezone } : {}),
      ...rest,
      ...formatPresetTimeFormat,
    });

    return formatted.replace(/\b(am|pm)\b/g, (match) => match.toUpperCase());
  }

  let format: Intl.DateTimeFormatOptions = {
    ...(timezone ? { timeZone: timezone } : {}),
  };

  if (showDate) {
    switch (dateFormat) {
      case 'd/m/yy':
      case '_2/_1/06':
        format = {
          ...format,
          year: '2-digit',
          month: 'numeric',
          day: 'numeric',
        };

        locale = 'id-id';
        break;
      case 'm/d/yy':
      case '_1/_2/06':
        format = {
          ...format,
          year: '2-digit',
          month: 'numeric',
          day: 'numeric',
        };

        locale = 'en-us';
        break;
      case 'd/mm/yy':
        format = {
          ...format,
          year: '2-digit',
          month: '2-digit',
          day: 'numeric',
        };
        break;
      case 'dd/mm/yyyy':
        format = {
          ...format,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        };
        break;
      case 'dd M yy':
      case '02 Jan 06':
        format = {
          ...format,
          year: '2-digit',
          month: 'short',
          day: '2-digit',
        };
        break;
      case 'dd M yyyy':
        format = {
          ...format,
          year: 'numeric',
          month: 'short',
          day: '2-digit',
        };
        break;
      case 'dd MM yy':
        format = {
          ...format,
          year: '2-digit',
          month: 'long',
          day: '2-digit',
        };
        break;
      case 'dd MM yyyy':
        format = {
          ...format,
          year: 'numeric',
          month: 'long',
          day: '2-digit',
        };
        break;
      case 'D dd M yy':
      case 'D, dd M yy':
      case 'Mon, 02 Jan 06':
        format = {
          ...format,
          weekday: 'short',
          year: '2-digit',
          month: 'short',
          day: '2-digit',
        };
        break;
      case 'D dd M yyyy':
      case 'D, dd M yyyy':
      case 'Mon, 02 Jan 2006':
        format = {
          ...format,
          weekday: 'short',
          year: 'numeric',
          month: 'short',
          day: '2-digit',
        };
        break;
      case 'DD dd MM yy':
      case 'DD, dd MM yy':
        format = {
          ...format,
          weekday: 'long',
          year: '2-digit',
          month: 'long',
          day: '2-digit',
        };
        break;
      case 'DD dd MM yyyy':
      case 'DD, dd MM yyyy':
        format = {
          ...format,
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: '2-digit',
        };
        break;
      case '02/01/06':
      default: // Format dd/mm/yy
        format = {
          ...format,
          year: '2-digit',
          month: '2-digit',
          day: '2-digit',
        };
        break;
    }
  }

  if (showTime && !timeFormat) timeFormat = 'HH:mm'; // Set default time format when showTime is true but timeFormat not provided

  /**
   * Show time when:
   * - `showTime` is provided
   * - `DateOptions.timeFormat` is provided, it will override general settings.
   */
  if (showTime || options.timeFormat) {
    switch (timeFormat) {
      case 'HH:mm':
      case 'HH:mm a':
      case '15:04':
      case '03:04 PM':
        format = {
          ...format,
          hour: '2-digit',
          minute: '2-digit',
          hour12: timeFormat.includes('a') || timeFormat.includes('PM'),
        };
        break;
      case 'HH:mm:ss':
      case 'HH:mm:ss a':
      case '15:04:05':
      case '03:04:05 PM':
        format = {
          ...format,
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: timeFormat.includes('a') || timeFormat.includes('PM'),
        };
        break;
      default:
        break;
    }
  }

  let formatted = dateFormat.includes(',')
    ? date.toLocaleString(locale, format)
    : date.toLocaleString(locale, format).replace(',', '');

  // Replace the hour-minute-second separator
  formatted = formatted.replace(/\./g, ':');

  return formatted.replace(/\b(am|pm)\b/g, (match) => match.toUpperCase());
};

export const formatISODate = (date: string | Date): string =>
  // Use Sweden locale, which uses ISO 8601 format: https://stackoverflow.com/a/58633651/27534858
  new Date(date).toLocaleString('sv');

/**
 * Formats a date to human readable form (timeago / relative time from now).
 *
 * @param date - The date to be displayed. Can be a Date object or a string in a recognized date format.
 * @param maxSeconds - Max value of seconds difference. Use it for formats a date according to the user's general settings.
 * @returns The formatted date string.
 */
export const formatDateReadable = (date: Date, maxSeconds?: number): string => {
  const seconds = Math.floor(
    (new Date().valueOf() - new Date(date).valueOf()) / 1000,
  );

  if (maxSeconds && seconds > maxSeconds) {
    return formatDate(date);
  }

  const interval = Math.floor(seconds / 31536000);
  if (interval >= 1) {
    return interval + ' tahun lalu';
  }

  const months = Math.floor(seconds / 2628000);
  if (months >= 1) {
    return months + ' bulan lalu';
  }

  const days = Math.floor(seconds / 86400);
  if (days >= 1) {
    return days + ' hari lalu';
  }

  const hours = Math.floor(seconds / 3600);
  if (hours >= 1) {
    return hours + ' jam lalu';
  }

  const minutes = Math.floor(seconds / 60);
  if (minutes >= 1) {
    return minutes + ' menit lalu';
  }

  return 'baru saja';
};

/**
 * A better "formatDateReadable"
 * @param date
 * @param maxSeconds
 * @param lang
 * @returns
 */
export const formatTimeAgo = (
  date: Date,
  maxSeconds?: number,
  lang = 'en',
): string => {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);

  if (maxSeconds && seconds > maxSeconds) {
    return formatDate(date);
  }

  const intervals: [number, Intl.RelativeTimeFormatUnit][] = [
    [Math.floor(seconds / 31536000), 'year'],
    [Math.floor(seconds / 2628000), 'month'],
    [Math.floor(seconds / 86400), 'day'],
    [Math.floor(seconds / 3600), 'hour'],
    [Math.floor(seconds / 60), 'minute'],
    [seconds, 'second'],
  ];

  const [value, unit] = intervals.find(([val]) => val > 0) ?? [0, 'second'];

  const rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' });
  const result = rtf.format(-value, unit);

  if (lang === 'id' && result.trim().toLowerCase() === 'sekarang') {
    return 'baru saja';
  }

  return result;
};

export const getRemainingTime = (
  deadline: string,
): {
  seconds: number;
  days: number;
  hours: number;
  minutes: number;
} => {
  const dateNow = new Date();
  const dateDeadline = new Date(deadline);

  const seconds = (dateDeadline.getTime() - dateNow.getTime()) / 1000;
  return {
    seconds,
    days: Math.floor(seconds / 86400),
    hours: Math.floor((seconds % 86400) / 3600),
    minutes: Math.floor((seconds % 3600) / 60),
  };
};

export const isISODateString = (value: string): boolean => {
  // This regex matches strict ISO 8601 UTC format (ends in "Z")
  const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/;

  return (
    typeof value === 'string' &&
    isoDateRegex.test(value) &&
    !isNaN(Date.parse(value))
  );
};
